import { useState } from 'react'
import {
  Accordion, AccordionDetails, AccordionSummary,
  Box, Button, Card, CardContent, Chip, Divider, FormControlLabel,
  Grid, Radio, RadioGroup, Stack, Typography,
} from '@mui/material'
import * as Icons from '@mui/icons-material'
import wordmark from '../assets/transparent-wordmark.png'
import { ParallaxHero } from './_components/parallax-hero'
import { useScrollReveal } from './use-scroll-reveal'
import { PageSeo } from './page-seo'

const SUPPORT_SCHEMA = {
  '@context': 'https://schema.org',
  '@type': 'WebPage',
  name: 'Support · TerraPlanner',
  url: 'https://terra-planner.com/support',
  description: 'Support TerraPlanner and keep it growing. Start free with pay-as-you-go top-ups, or subscribe as a Perennial Supporter for premium features.',
  offers: [
    { '@type': 'Offer', name: 'Free', price: '0', priceCurrency: 'USD' },
    { '@type': 'Offer', name: 'Perennial Supporter', description: 'Monthly subscription — choose your amount, access all premium and beta features' },
  ],
}

const FREE_FEATURES = [
  { icon: <Icons.GridViewTwoTone />, text: 'Comprehensive dashboard — timeline, map, and list in one place' },
  { icon: <Icons.YardTwoTone />, text: 'Full crop visibility — schedules, tasks, and harvest windows' },
  { icon: <Icons.PublicTwoTone />, text: 'Farm anywhere — real climate data from Copernicus CDS' },
  { icon: <Icons.TaskAltTwoTone />, text: 'Dynamic scheduling — timelines adapt as you complete tasks' },
  { icon: <Icons.AutorenewTwoTone />, text: 'Succession planting — staggered harvests across any growth type' },
  { icon: <Icons.ManageSearchTwoTone />, text: 'Storage management — keep what matters, clear the rest' },
]

const PREMIUM_FEATURES = [
  { icon: <Icons.InsightsTwoTone />, text: 'Insights & metrics — yield stats, plot areas, GDD tracking' },
  { icon: <Icons.SupportAgentTwoTone />, text: 'Priority support' },
  { icon: <Icons.ScienceTwoTone />, text: 'Beta features — early access to new tools as they ship' },
]

const PRICE_OPTIONS = [6, 8, 10]

const HeroSection = () => (
  <ParallaxHero>
    <Box
      component="img"
      src={wordmark}
      alt="TerraPlanner"
      width={200}
      height={50}
      sx={{ height: { xs: 56, md: 72 }, width: 'auto', mb: 1 }}
    />
    <Typography
      component="h1"
      variant="h5"
      fontWeight={400}
      sx={{ maxWidth: 640, mx: 'auto', mb: 3, opacity: 0.92, lineHeight: 1.5, fontSize: { xs: '1.1rem', md: '1.35rem' } }}
    >
      Support TerraPlanner
    </Typography>
    <Typography
      variant="body1"
      sx={{ maxWidth: 580, mx: 'auto', opacity: 0.82, lineHeight: 1.75, fontSize: { xs: '0.95rem', md: '1rem' } }}
    >
      TerraPlanner is built by farmers, for farmers — including ours. Supporting the platform helps keep it
      growing so we can support yours. Start free with pay-as-you-go top-ups, or subscribe and grow with us.
    </Typography>
  </ParallaxHero>
)

export const FreeTierCard = ({ index, onSelect }) => {
  const { ref, visible } = useScrollReveal(0.15)

  return (
    <Card
      ref={ref}
      sx={{
        height: '100%',
        opacity: visible ? 1 : 0,
        transform: visible ? 'translateY(0)' : 'translateY(24px)',
        transition: `opacity 0.5s ease ${index * 0.08}s, transform 0.5s ease ${index * 0.08}s`,
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <CardContent sx={{ p: 3, display: 'flex', flexDirection: 'column', gap: 2, flexGrow: 1 }}>
        <Box sx={{ color: 'primary.main' }}>
          <Icons.SavingsTwoTone fontSize="large" />
        </Box>
        <Box>
          <Typography variant="h5" fontWeight={700}>Free</Typography>
          <Typography variant="body2" color="text.secondary" mt={0.5}>Always free, no catches.</Typography>
        </Box>
        <Typography variant="h6" fontWeight={600} color="primary">
          $0 <Typography component="span" variant="body2" color="text.secondary">— always</Typography>
        </Typography>
        <Divider />
        <Stack spacing={1.5} sx={{ flexGrow: 1 }}>
          {[
            'Basic storage credit included, no expiry',
            'Full access to all core features',
            'Top up storage any time — no subscription required',
            'Manage your data to keep what matters most',
            'Reach the Perennial Supporter threshold in a calendar year and unlock premium features',
          ].map((item) => (
            <Stack key={item} direction="row" spacing={1} alignItems="flex-start">
              <Icons.CheckCircleOutlineTwoTone fontSize="small" sx={{ color: 'text.secondary', mt: '2px', flexShrink: 0 }} />
              <Typography variant="body2" color="text.secondary">{item}</Typography>
            </Stack>
          ))}
        </Stack>
        <Button
          variant="outlined"
          color="primary"
          fullWidth
          disabled={!onSelect}
          onClick={onSelect}
        >
          Get Started
        </Button>
      </CardContent>
    </Card>
  )
}

export const PerennialCard = ({ index, onSelect }) => {
  const { ref, visible } = useScrollReveal(0.15)
  const [expanded, setExpanded] = useState(false)
  const [price, setPrice] = useState('')
  const [frequency, setFrequency] = useState('monthly')

  const handleSelectClick = () => setExpanded(true)
  const handleConfirm = () => {
    if (price && frequency) onSelect({ price: Number(price), frequency })
  }

  return (
    <Card
      ref={ref}
      sx={{
        height: '100%',
        opacity: visible ? 1 : 0,
        transform: visible ? 'translateY(0)' : 'translateY(24px)',
        transition: `opacity 0.5s ease ${index * 0.08}s, transform 0.5s ease ${index * 0.08}s`,
        border: (t) => `1px solid ${t.palette.primary.main}`,
      }}
    >
      <CardContent
        sx={{
          p: 3,
          height: '100%',
          display: 'flex',
          flexDirection: { xs: 'column', md: 'row' },
          gap: { xs: 2, md: 4 },
        }}
      >
        {/* Left column */}
        <Stack spacing={2} sx={{ flex: '0 0 auto', minWidth: { md: 220 } }}>
          <Stack direction="row" alignItems="center" justifyContent="space-between">
            <Box sx={{ color: 'primary.main' }}>
              <Icons.AutorenewTwoTone fontSize="large" />
            </Box>
            <Chip label="Premium" color="primary" size="small" />
          </Stack>
          <Box>
            <Typography variant="h5" fontWeight={700}>Perennial Supporter</Typography>
            <Typography variant="body2" color="text.secondary" mt={0.5}>Subscribe and grow with us.</Typography>
          </Box>
          <Typography variant="h6" fontWeight={600} color="primary">
            Monthly
          </Typography>

          {onSelect ? (
            <Stack spacing={1} sx={{ mt: 'auto' }}>
              <Button
                variant="contained"
                color="primary"
                onClick={handleSelectClick}
                disabled={expanded}
              >
                Select Premium
              </Button>

              <Accordion
                expanded={expanded}
                disableGutters
                elevation={0}
                sx={{ border: 'none', '&::before': { display: 'none' } }}
              >
                <AccordionSummary sx={{ display: 'none' }} />
                <AccordionDetails sx={{ px: 0, pt: 1, pb: 0 }}>
                  <Stack spacing={2}>
                    <Box>
                      <Typography variant="caption" color="text.secondary" fontWeight={600}>
                        Monthly amount
                      </Typography>
                      <RadioGroup value={price} onChange={(e) => setPrice(e.target.value)}>
                        {PRICE_OPTIONS.map((p) => (
                          <FormControlLabel
                            key={p}
                            value={String(p)}
                            control={<Radio size="small" />}
                            label={<Typography variant="body2">${p} / month</Typography>}
                          />
                        ))}
                      </RadioGroup>
                    </Box>
                    <Box>
                      <Typography variant="caption" color="text.secondary" fontWeight={600}>
                        Billing frequency
                      </Typography>
                      <RadioGroup value={frequency} onChange={(e) => setFrequency(e.target.value)}>
                        <FormControlLabel value="monthly" control={<Radio size="small" />} label={<Typography variant="body2">Monthly</Typography>} />
                        <FormControlLabel value="annually" control={<Radio size="small" />} label={<Typography variant="body2">Annually</Typography>} />
                      </RadioGroup>
                    </Box>
                    <Button
                      variant="contained"
                      color="primary"
                      fullWidth
                      disabled={!price}
                      onClick={handleConfirm}
                    >
                      Confirm
                    </Button>
                  </Stack>
                </AccordionDetails>
              </Accordion>
            </Stack>
          ) : (
            <Button variant="contained" color="primary" sx={{ mt: 'auto' }} disabled>
              Subscribe
            </Button>
          )}
        </Stack>

        <Divider orientation="vertical" flexItem sx={{ display: { xs: 'none', md: 'block' } }} />
        <Divider sx={{ display: { xs: 'block', md: 'none' } }} />

        {/* Right column */}
        <Stack spacing={1.5} justifyContent="center" sx={{ flexGrow: 1 }}>
          {[
            'Choose your own monthly amount — above the minimum threshold',
            'Each payment adds the equivalent credit to your storage balance',
            'Access to all premium and beta features',
            'Cancel any time — previously purchased storage remains yours',
          ].map((item) => (
            <Stack key={item} direction="row" spacing={1} alignItems="flex-start">
              <Icons.CheckCircleOutlineTwoTone fontSize="small" sx={{ color: 'primary.main', mt: '2px', flexShrink: 0 }} />
              <Typography variant="body2" color="text.secondary">{item}</Typography>
            </Stack>
          ))}
        </Stack>
      </CardContent>
    </Card>
  )
}

export const FeaturesCard = () => {
  const { ref, visible } = useScrollReveal(0.1)

  return (
    <Box sx={{ px: { xs: 3, md: 6 }, pb: { xs: 8, md: 10 } }}>
      <Card
        ref={ref}
        sx={{
          maxWidth: 900,
          mx: 'auto',
          opacity: visible ? 1 : 0,
          transform: visible ? 'translateY(0)' : 'translateY(24px)',
          transition: 'opacity 0.5s ease, transform 0.5s ease',
        }}
      >
        <CardContent sx={{ p: { xs: 3, md: 4 } }}>
          <Typography variant="overline" color="primary" fontWeight={600} letterSpacing={2}>
            What's included
          </Typography>
          <Typography variant="h5" fontWeight={700} mt={0.5} mb={3}>
            Free & premium features
          </Typography>

          <Grid container spacing={{ xs: 3, md: 5 }}>
            <Grid size={{ xs: 12, md: 6 }}>
              <Typography variant="subtitle2" fontWeight={700} color="text.secondary" gutterBottom sx={{ textTransform: 'uppercase', letterSpacing: 1 }}>
                Free tier
              </Typography>
              <Stack spacing={1.5} mt={1.5}>
                {FREE_FEATURES.map(({ icon, text }) => (
                  <Stack key={text} direction="row" spacing={1.5} alignItems="flex-start">
                    <Box sx={{ color: 'text.secondary', mt: '2px', flexShrink: 0, '& svg': { fontSize: '1.1rem' } }}>{icon}</Box>
                    <Typography variant="body2" color="text.secondary">{text}</Typography>
                  </Stack>
                ))}
              </Stack>
            </Grid>

            <Grid size={{ xs: 12, md: 6 }}>
              <Typography variant="subtitle2" fontWeight={700} color="primary" gutterBottom sx={{ textTransform: 'uppercase', letterSpacing: 1 }}>
                Premium, plus everything above
              </Typography>
              <Stack spacing={1.5} mt={1.5}>
                {PREMIUM_FEATURES.map(({ icon, text }) => (
                  <Stack key={text} direction="row" spacing={1.5} alignItems="flex-start">
                    <Box sx={{ color: 'primary.main', mt: '2px', flexShrink: 0, '& svg': { fontSize: '1.1rem' } }}>{icon}</Box>
                    <Typography variant="body2" color="text.secondary">{text}</Typography>
                  </Stack>
                ))}
              </Stack>
              <Box
                sx={{
                  mt: 3,
                  p: 2,
                  borderRadius: 1,
                  bgcolor: 'action.hover',
                  border: (t) => `1px dashed ${t.palette.divider}`,
                }}
              >
                <Typography variant="body2" color="text.secondary" fontStyle="italic">
                  Screenshot gallery coming soon — see the dashboard, timeline, and map in action.
                </Typography>
              </Box>
            </Grid>
          </Grid>
        </CardContent>
      </Card>
    </Box>
  )
}

const SupportSection = () => {
  const { ref, visible } = useScrollReveal(0.1)

  return (
    <Box sx={{ px: { xs: 3, md: 6 }, py: { xs: 8, md: 10 } }}>
      <Box
        ref={ref}
        sx={{
          textAlign: 'center',
          mb: 6,
          opacity: visible ? 1 : 0,
          transform: visible ? 'translateY(0)' : 'translateY(20px)',
          transition: 'opacity 0.5s ease, transform 0.5s ease',
        }}
      >
        <Typography variant="overline" color="primary" fontWeight={600} letterSpacing={2}>
          Support
        </Typography>
        <Typography variant="h4" fontWeight={700} mt={0.5}>
          One model, two paths.
        </Typography>
        <Typography variant="body1" color="text.secondary" mt={1} sx={{ maxWidth: 520, mx: 'auto' }}>
          Every account starts free. Top up with storage credits when you need more space, or subscribe
          as a Perennial Supporter for premium features and a monthly storage boost.
        </Typography>
      </Box>

      <Grid container spacing={3} sx={{ maxWidth: 1100, mx: 'auto', alignItems: 'stretch' }}>
        <Grid size={{ xs: 12, md: 6 }}>
          <FreeTierCard index={0} />
        </Grid>
        <Grid size={{ xs: 12, md: 6 }}>
          <PerennialCard index={1} />
        </Grid>
      </Grid>
    </Box>
  )
}

export const Support = () => {
  return (
    <Box>
      <PageSeo
        title="Support"
        description="Support TerraPlanner and keep it growing. Start free with pay-as-you-go top-ups, or subscribe as a Perennial Supporter for premium features."
        path="/support"
        schema={SUPPORT_SCHEMA}
      />
      <HeroSection />
      <SupportSection />
      <FeaturesCard />
    </Box>
  )
}
